import { create } from 'zustand';
import { MAX_AGE, MIN_AGE, type Gender, type GenderPreference, type Topic, type UserDto } from '@anon-chat/shared';
import { ApiError, api } from '@/shared/api';
import { botWriteAccess, getInitData, requestWriteAccess } from '@/shared/telegram';

export type AuthStatus = 'idle' | 'authenticating' | 'ready' | 'error';

/** Выбор в онбординге до отправки на сервер. */
export interface DraftPreferences {
  gender: Gender | null;
  /** Свой возраст, 18–99. */
  age: number;
  topic: Topic | null;
  preferredGender: GenderPreference | null;
  /** Желаемый возраст собеседника. */
  preferredAgeMin: number;
  preferredAgeMax: number;
}

interface SessionState {
  status: AuthStatus;
  user: UserDto | null;
  /**
   * Может ли бот писать этому человеку. `undefined` — старый клиент Telegram,
   * который об этом не сообщает; отсутствие ответа не повод считать, что нельзя.
   */
  botCanWrite: boolean | undefined;
  errorCode: string | null;
  draft: DraftPreferences;

  login: () => Promise<void>;
  loginAsGuest: () => Promise<boolean>;
  setDraft: (patch: Partial<DraftPreferences>) => void;
  resetDraft: () => void;
  setUser: (user: UserDto) => void;
  /** Переключатель напоминаний бота. Возвращает состояние после переключения. */
  toggleNotifications: () => Promise<boolean>;
  /** Просит у Telegram разрешение боту писать. Возвращает, дали ли его. */
  askBotWriteAccess: () => Promise<boolean>;
  /** Принимает сессию, полученную обходным путём — входом через бота. */
  adoptSession: (user: UserDto) => void;
}

/** Значения по умолчанию: середина шкалы и полный диапазон поиска. */
const emptyDraft: DraftPreferences = {
  gender: null,
  age: 25,
  topic: null,
  preferredGender: null,
  preferredAgeMin: MIN_AGE,
  preferredAgeMax: MAX_AGE,
};

export const useSessionStore = create<SessionState>((set, get) => ({
  status: 'idle',
  user: null,
  botCanWrite: botWriteAccess(),
  errorCode: null,
  draft: emptyDraft,

  async login() {
    if (get().status === 'authenticating') {
      return;
    }
    set({ status: 'authenticating', errorCode: null });

    try {
      const { user } = await api.loginWithTelegram(getInitData());
      set({
        status: 'ready',
        user,
        // К моменту входа SDK точно готов: до него значение могло быть ещё не
        // прочитано.
        botCanWrite: botWriteAccess(),
        // Уже сохранённые параметры подставляются в онбординг: повторный вход
        // не должен заставлять выбирать всё заново.
        draft: {
          gender: user.gender,
          age: user.age ?? emptyDraft.age,
          topic: user.topic,
          preferredGender: user.preferredGender,
          preferredAgeMin: user.preferredAgeMin ?? MIN_AGE,
          preferredAgeMax: user.preferredAgeMax ?? MAX_AGE,
        },
      });
    } catch (error) {
      set({
        status: 'error',
        errorCode: error instanceof ApiError ? error.code : 'INTERNAL_ERROR',
      });
    }
  },

  async loginAsGuest() {
    if (get().status === 'authenticating') {
      return false;
    }
    set({ status: 'authenticating', errorCode: null });

    try {
      const { user } = await api.loginAsGuest();
      set({
        status: 'ready',
        user,
        draft: {
          gender: user.gender,
          age: user.age ?? emptyDraft.age,
          topic: user.topic,
          preferredGender: user.preferredGender,
          preferredAgeMin: user.preferredAgeMin ?? MIN_AGE,
          preferredAgeMax: user.preferredAgeMax ?? MAX_AGE,
        },
      });
      return true;
    } catch (error) {
      set({
        status: 'error',
        errorCode: error instanceof ApiError ? error.code : 'INTERNAL_ERROR',
      });
      return false;
    }
  },

  setDraft(patch) {
    set((state) => ({ draft: { ...state.draft, ...patch } }));
  },

  resetDraft() {
    set({ draft: emptyDraft });
  },

  setUser(user) {
    set({ user });
  },

  adoptSession(user) {
    set({
      status: 'ready',
      errorCode: null,
      user,
      draft: {
        gender: user.gender,
        age: user.age ?? emptyDraft.age,
        topic: user.topic,
        preferredGender: user.preferredGender,
        preferredAgeMin: user.preferredAgeMin ?? MIN_AGE,
        preferredAgeMax: user.preferredAgeMax ?? MAX_AGE,
      },
    });
  },

  async askBotWriteAccess() {
    const granted = await requestWriteAccess();
    if (granted) {
      set({ botCanWrite: true });
    }
    return granted;
  },

  /*
    Состояние меняется до ответа сервера: переключатель обязан откликаться
    мгновенно. Отказ возвращает всё назад — иначе человек уйдёт с экрана в
    уверенности, что уведомления выключены, а бот продолжит писать.
  */
  async toggleNotifications() {
    const current = get().user;
    if (!current) {
      return false;
    }

    const next = !current.notificationsEnabled;
    set({ user: { ...current, notificationsEnabled: next } });

    try {
      await api.setNotifications(next);
      return next;
    } catch {
      set({ user: { ...current, notificationsEnabled: current.notificationsEnabled } });
      return current.notificationsEnabled;
    }
  },
}));
